// src/features/commission/CommissionPage.tsx
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { useState } from 'react';
import { CheckCircle2, CircleDollarSign, Clock3, WalletCards } from 'lucide-react';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { CommissionTable, CommissionHeader } from './components';
import { useCommissions } from './hooks/useCommissions';
import './styles/commission.css';

function normalizeMobileSearchValue(value: string) {
  return value
    .trim()
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));
}

/**
 * Commission Foundation. Same layout pattern as OrdersPage/
 * ServicesPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Every row here comes from features/commission/data/commission.mock.ts
 * via useCommissionMock() — no commission endpoint exists yet.
 * useCommissionMock is the seam a later phase swaps for a real API
 * hook (same shape, `{ commissions }`); CommissionTable only depends
 * on the `commissions` prop, not on where it came from, so that swap
 * won't touch this file or CommissionTable.tsx.
 */
export function CommissionPage() {
  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<'representativeId' | 'mobile' | 'tenantId'>('representativeId');
  const [searchValue, setSearchValue] = useState('');
  const filters = {
    representativeId: searchType === 'representativeId' ? searchValue : '',
    mobile: searchType === 'mobile' ? normalizeMobileSearchValue(searchValue) : '',
    tenantId: searchType === 'tenantId' ? searchValue : '',
  };
  const { commissions, meta, summary, loading, error } = useCommissions(page, filters);
  const totalPages = Math.max(1, Math.ceil(meta.total / meta.limit));
  const initialLoading = loading && commissions.length === 0 && !summary;

  return (
    <ControlPlanePage
      className="vania-commission-control-plane-page"
      title="مدیریت کمیسیون‌ها"
      summary={initialLoading ? <CommissionSummarySkeleton /> : summary ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'کل درآمد', value: `${formatPersianNumber(summary.earned_total ?? 0)} تومان`, icon: <CircleDollarSign size={16} />, tone: 'primary' },
            { label: 'پرداخت‌شده', value: `${formatPersianNumber(summary.paid_total ?? 0)} تومان`, icon: <CheckCircle2 size={16} />, tone: 'success' },
            { label: 'در انتظار', value: `${formatPersianNumber(summary.pending_total ?? 0)} تومان`, icon: <Clock3 size={16} />, tone: 'warning' },
            { label: 'موجودی قابل پرداخت', value: `${formatPersianNumber(summary.available_balance ?? 0)} تومان`, icon: <WalletCards size={16} />, tone: 'danger' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          {error && commissions.length === 0 && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          <div className="vania-users-toolbar vania-commission-toolbar">
            <div className="vania-users-search vania-commission-filter-group" role="search" aria-label="فیلترهای کمیسیون">
              <input type={searchType === 'mobile' ? 'tel' : 'search'} value={searchValue} onChange={(e) => { setSearchValue(e.target.value); setPage(1); }} placeholder="شناسه نماینده، موبایل نماینده یا شناسه تننت را جستجو کنید" aria-label="جست‌وجوی کمیسیون" />
              <select value={searchType} onChange={(e) => { setSearchType(e.target.value as typeof searchType); setSearchValue(''); setPage(1); }} aria-label="نوع جست‌وجوی کمیسیون">
                <option value="representativeId">شناسه نماینده</option>
                <option value="mobile">موبایل نماینده</option>
                <option value="tenantId">شناسه تننت</option>
              </select>
            </div>
          </div>
          <div className="vania-commission-table-region">
            {error && commissions.length === 0 ? null : initialLoading ? <CommissionTableSkeleton /> : !loading && commissions.length === 0 ? <EmptyState message="کمیسیونی یافت نشد." /> : <CommissionTable commissions={commissions} />}
          </div>
          {commissions.length > 0 ? <div className="vania-commission-pagination"><button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>قبلی</button><span>صفحه {formatPersianNumber(page)} از {formatPersianNumber(totalPages)}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>بعدی</button></div> : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function CommissionSummarySkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری خلاصه کمیسیون‌ها">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4 vania-commission-summary-skeleton" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={`commission-summary-skeleton-${index}`} className="vania-commission-summary-card">
            <VaniaContentSkeleton style={{ width: '42%', height: '0.75rem' }} />
            <VaniaContentSkeleton style={{ width: '68%', height: '1.2rem' }} />
          </div>
        ))}
      </div>
    </VaniaLoadingSystem>
  );
}

function CommissionTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری کمیسیون‌ها">
      <div className="vania-commission-table-scroll" aria-hidden="true">
          <table className="vania-commission-table">
            <thead>
              <tr>
                {['نماینده', 'شناسه', 'موبایل', 'تننت', 'شناسه تننت', 'مبلغ', 'وضعیت', 'تاریخ', 'عملیات'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 20 }, (_, rowIndex) => (
                <tr key={`commission-skeleton-${rowIndex}`}>
                  {Array.from({ length: 9 }, (_, cellIndex) => (
                    <td key={`commission-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </VaniaLoadingSystem>
  );
}
